import React, { useState, useContext, useEffect, ReactNode } from "react"
import { PricesQuery } from "../../types/graphql-types"
import { StaticQuery, graphql } from "gatsby"
import LocaleContext from "../context/localeContext"
import { getLocaleMatchedCurrency } from "../utils/currencyList"
import Cookies from "js-cookie"

export interface PricesProps {
  packagePrice: number
  packageUpgradePrice: number
  simPrice: number
  simAdditionalPrice: number
  priceEsim: number
  currency: string
  currencySymbol: string
}
export type CurrencyName = "USD" | "EUR" | "GBP" | "BRL"

export interface PriceContextType {
  priceData: PricesProps
  currencyName: CurrencyName
  setPriceData: (value: PricesProps) => void
  setCurrencyName: (value: CurrencyName) => void
}

export const PriceContext = React.createContext<PriceContextType | null>(null)

export const getPricesFromData = (currencyName: CurrencyName, data: PricesQuery): PricesProps => {
  const eurPackages = data!.EUR!.packageProducts?.packages!
  const usdPackages = data!.USD!.packageProducts?.packages!
  const gbpPackages = data!.GBP!.packageProducts?.packages!
  const brlPackages = data!.BRL!.packageProducts?.packages!
  const eurSimProduct = data!.EUR!.simProducts!
  const usdSimProduct = data!.USD!.simProducts!
  const gbpSimProduct = data!.GBP!.simProducts!
  const brlSimProduct = data!.BRL!.simProducts!

  switch (currencyName) {
    case "EUR":
      return {
        packagePrice: eurPackages[0]!.price!,
        packageUpgradePrice: eurPackages[1]?.upgradePrice!,
        simPrice: eurSimProduct.price!,
        simAdditionalPrice: eurSimProduct.priceAdditional!,
        priceEsim: eurSimProduct.priceEsim!,
        currency: eurPackages[0]!.currency!,
        currencySymbol: eurPackages[0]!.currencySymbol!,
      }
    case "GBP":
      return {
        packagePrice: gbpPackages[0]!.price!,
        packageUpgradePrice: gbpPackages[1]?.upgradePrice!,
        simPrice: gbpSimProduct.price!,
        simAdditionalPrice: gbpSimProduct.priceAdditional!,
        priceEsim: gbpSimProduct.priceEsim!,
        currency: gbpPackages[0]!.currency!,
        currencySymbol: gbpPackages[0]!.currencySymbol!,
      }
    case "USD":
      return {
        packagePrice: usdPackages[0]!.price!,
        packageUpgradePrice: usdPackages[1]?.upgradePrice!,
        simPrice: usdSimProduct.price!,
        simAdditionalPrice: usdSimProduct.priceAdditional!,
        priceEsim: usdSimProduct.priceEsim!,
        currency: usdPackages[0]!.currency!,
        currencySymbol: usdPackages[0]!.currencySymbol!,
      }
    case "BRL":
      return {
        packagePrice: brlPackages[0]!.price!,
        packageUpgradePrice: brlPackages[1]?.upgradePrice!,
        simPrice: brlSimProduct.price!,
        simAdditionalPrice: brlSimProduct.priceAdditional!,
        priceEsim: brlSimProduct.priceEsim!,
        currency: brlPackages[0]!.currency!,
        currencySymbol: brlPackages[0]!.currencySymbol!,
      }
  }
}
interface PriceContextProviderComponentProps {
  data: PricesQuery
}
const PriceContextProviderComponent: React.FC<PriceContextProviderComponentProps> = ({
  children,
  data,
}) => {
  const locale = useContext<string>(LocaleContext)
  const [currencyName, setCurrencyName] = useState<CurrencyName>(getLocaleMatchedCurrency(locale))
  const [priceData, setPriceData] = useState<PricesProps>(getPricesFromData(currencyName, data))
  useEffect(() => {
    const userSelectedCurrency = localStorage.getItem("UserSelectedCurrency") as CurrencyName
    if (!userSelectedCurrency) {
      const region = Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone.split("/")[0]
      // get user location from cookie
      const locationCookie: string | undefined = Cookies.get("hmdconnect_location")
      const location = locationCookie?.toLocaleLowerCase()
      setCurrencyName(getLocaleMatchedCurrency(location || locale, region))
    } else {
      setCurrencyName(userSelectedCurrency)
    }
  }, [])

  useEffect(() => {
    setPriceData(getPricesFromData(currencyName, data))
  }, [currencyName])

  return (
    <PriceContext.Provider value={{ priceData, setPriceData, currencyName, setCurrencyName }}>
      {children}
    </PriceContext.Provider>
  )
}

export default (props: { children: ReactNode }) => (
  <StaticQuery
    query={graphql`
      query Prices {
        BRL: restBrlPrices {
          packageProducts {
            packages {
              price
              upgradePrice
              currency
              currencySymbol
            }
          }
          simProducts {
            price
            priceAdditional
            priceEsim
          }
        }
        USD: restUsdPrices {
          packageProducts {
            packages {
              price
              upgradePrice
              currency
              currencySymbol
            }
          }
          simProducts {
            price
            priceAdditional
            priceEsim
          }
        }
        EUR: restEurPrices {
          packageProducts {
            packages {
              price
              upgradePrice
              currency
              currencySymbol
            }
          }
          simProducts {
            price
            priceAdditional
            priceEsim
          }
        }
        GBP: restGbpPrices {
          packageProducts {
            packages {
              price
              upgradePrice
              currency
              currencySymbol
            }
          }
          simProducts {
            price
            priceAdditional
            priceEsim
          }
        }
      }
    `}
    render={data => <PriceContextProviderComponent data={data} {...props} />}
  />
)
